import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useMediaConfig } from '@/hooks/useMediaConfig';
import type { MediaConfig } from '@/api/media';
import type { MessageAttachment } from '@/types';
import { filesFromClipboard } from '@/utils/clipboard';
import { StagedAttachments } from './StagedAttachments';

interface ComposerProps {
  variant: 'customer' | 'guidance';
  /** Placeholder text (already localized + interpolated by the caller). */
  placeholder: string;
  /**
   * Sends the composed message. `attachmentIds` are server-side media ids the agent already
   * uploaded. On the `guidance` variant they are PICTURES for the bot, and `attachments` carries
   * their display metadata too (for the agent's own optimistic echo in the consultation thread -
   * never sent to the server, which re-resolves every id itself).
   */
  onSend: (text: string, attachmentIds?: string[], attachments?: MessageAttachment[]) => void | Promise<void>;
  /** Fired (throttled) as the agent types — used to relay the typing signal. Customer variant ONLY. */
  onTyping?: () => void;
  /**
   * Bot voice Phase D (2026-10-31) - guidance variant ONLY. Fired when the guidance text CHANGES (not on a prefill, not
   * when the box is cleared after Send), at most once every {@link GUIDANCE_COMPOSING_THROTTLE_MS}. It tells the server a
   * teammate is still writing guidance, so the customer's next bot turn waits. It is never the customer typing signal:
   * the guidance variant never calls `onTyping`.
   */
  onGuidanceComposing?: () => void;
  disabled?: boolean;
  /**
   * Session the attachments belong to. Required for uploading; without it the paperclip stays
   * hidden, since the media service scopes every upload to a session.
   */
  sessionId?: string;
  /**
   * Visible label for the send button (console v2 AnswerBar: "Send to customer" / "Send to bot"). Without it the
   * button stays the icon-only square it has always been. The accessible name is the label either way.
   */
  sendLabel?: string;
  /**
   * Colour of a labelled send button: `go` (green, reaches the customer) or `stop` (red, internal - the customer
   * never sees it). Only applied together with `sendLabel`; unlabelled composers keep their variant colours.
   */
  tone?: 'go' | 'stop';
  /**
   * First chance to explain a failed send in the caller's own words (e.g. "this conversation was just taken").
   * Return undefined to fall through to the generic reason codes below. Must be pure - it runs during the catch.
   */
  describeError?: (err: unknown) => string | undefined;
  /**
   * 2026-10-28 - text to put into the box for the agent to change (Edit on a proposed reply). Applied ONCE per `seq`
   * (replacing what was typed), then `onPrefillApplied(seq)` is called so the caller can drop it - a remounted composer
   * must never re-apply an old one. Nothing is sent: the agent still presses Send.
   */
  prefill?: { text: string; seq: number } | null;
  onPrefillApplied?: (seq: number) => void;
  'data-testid'?: string;
}

/**
 * Turns a failed send into something an agent can act on.
 *
 * AgentHub throws HubException carrying a machine reason code, and SignalR delivers that text
 * to the client verbatim. Matching on substrings rather than parsing is deliberate: the client
 * error string is wrapped by SignalR ("An unexpected error occurred invoking '…' on the server.
 * HubException: resolve_not_clean"), and an unrecognised code must still produce a usable
 * sentence rather than a blank.
 */
function describeSendFailure(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? '');

  if (raw.includes('attachments_disabled'))
    return 'Attachments are not enabled for this tenant. Send the message without files, or ask an administrator to turn them on.';
  if (raw.includes('attachments_images_only'))
    return 'Only pictures can be sent to the bot. Remove the other files and try again.';
  if (raw.includes('attachments_unavailable'))
    return 'Attachments are temporarily unavailable. Your message was not sent — try again shortly.';
  if (raw.includes('resolve_not_clean'))
    return 'One of the files did not pass the malware scan, so nothing was sent. Remove it and try again.';
  if (raw.includes('resolve_http_404') || raw.includes('resolve_mismatch'))
    return 'One of the files is no longer available on this conversation. Remove it and re-attach.';
  if (raw.includes('resolve_unavailable') || raw.includes('resolve_malformed'))
    return 'The file service could not be reached, so nothing was sent. Your files are still attached — try again.';
  if (raw.includes('at most'))
    return 'Too many attachments on one message. Remove some and try again.';
  if (raw.includes('not connected'))
    return 'Not connected to the agent hub. Your message was not sent — it will work once the connection returns.';

  return 'The message could not be sent. Your text and files are still here — try again.';
}

/**
 * Least time between two guidance-composing notifications (bot voice Phase D). The server hint lives 6 s, so 2 s keeps
 * it alive while the teammate types and lets it lapse soon after they stop.
 */
export const GUIDANCE_COMPOSING_THROTTLE_MS = 500;

/** The picture types the bot can read. Used when the server advertises no restriction of its own. */
const GUIDANCE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/**
 * The upload policy for guidance: PICTURES ONLY, and never wider than the server's own policy. An allow-list with no
 * image type in it turns guidance attachments off rather than falling back to "anything" (an empty list means "no
 * restriction" to the staging validator, so it must never be produced by filtering).
 */
export function guidanceMediaConfig(cfg: MediaConfig | undefined): MediaConfig {
  // Fail closed on anything not positively enabled (including a policy that has not loaded or came back malformed).
  if (!cfg?.enabled) return { enabled: false, maxFileSizeBytes: cfg?.maxFileSizeBytes ?? 0, allowedMimeTypes: [] };
  const allowed = Array.isArray(cfg.allowedMimeTypes) ? cfg.allowedMimeTypes : [];
  const images =
    allowed.length > 0
      ? allowed.filter((t) => typeof t === 'string' && t.toLowerCase().startsWith('image/'))
      : GUIDANCE_IMAGE_TYPES;
  return images.length > 0
    ? { ...cfg, allowedMimeTypes: images }
    : { ...cfg, enabled: false, allowedMimeTypes: [] };
}

/**
 * Message composer. `customer` variant sends a reply to the customer (during takeover);
 * `guidance` variant advises the bot (consultation). Enter sends, Shift+Enter inserts a newline;
 * the textarea auto-grows to 120px.
 *
 * 2026-10-20 - both variants can attach. On `guidance` the paperclip takes PICTURES only: they
 * are for the bot, which looks at them while it writes its reply and sends them to the customer
 * with that reply when the channel can carry a file. Same switch, same upload, same limits as the
 * customer variant; the server (AgentHub.SendGuidanceWithAttachments) re-checks every file and
 * refuses anything that is not a picture of this conversation.
 */
export const Composer: React.FC<ComposerProps> = ({
  variant,
  placeholder,
  onSend,
  onTyping,
  onGuidanceComposing,
  disabled,
  sessionId,
  sendLabel,
  tone,
  describeError,
  prefill,
  onPrefillApplied,
  ...rest
}) => {
  const { t } = useT();
  const consult = variant === 'guidance';
  const [v, setV] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastTypingRef = useRef(0);
  // null = never notified yet, so the first keystroke always notifies (a fake clock may start at 0).
  const lastGuidanceComposingRef = useRef<number | null>(null);

  // 2026-10-28 - Edit on a proposed reply: the text lands in the box, focused, sized to fit; the caller drops it once
  // applied. The callback is read through a ref so a new identity never re-applies the same text.
  const prefillSeq = prefill?.seq;
  const prefillText = prefill?.text;
  const onPrefillAppliedRef = useRef(onPrefillApplied);
  onPrefillAppliedRef.current = onPrefillApplied;
  const [resizeTick, setResizeTick] = useState(0);
  useEffect(() => {
    if (prefillSeq === undefined || prefillText === undefined) return;
    setV(prefillText);
    setSendError(null);
    setResizeTick((n) => n + 1);
    onPrefillAppliedRef.current?.(prefillSeq);
  }, [prefillSeq, prefillText]);
  useEffect(() => {
    if (resizeTick === 0) return;
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    el.focus();
  }, [resizeTick]);

  const serverMediaConfig = useMediaConfig();
  // Guidance takes pictures only; the staging validator enforces it on pick AND paste.
  const mediaConfig = useMemo(
    () => (consult ? guidanceMediaConfig(serverMediaConfig) : serverMediaConfig),
    [consult, serverMediaConfig],
  );
  const { staged, addFiles, removeStaged, clearStaged, readyIds, isUploading, canStageMore } =
    useAttachmentStaging(sessionId, mediaConfig);

  // Ships dark (the server's media policy), and needs a session: uploads are session-scoped.
  const attachmentsAvailable = mediaConfig.enabled && !!sessionId;

  const grow = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setV(next);
    const el = ref.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
    // Throttle the typing signal to ~1/sec (customer variant only).
    if (!consult && onTyping) {
      const now = Date.now();
      if (now - lastTypingRef.current > 1000) {
        lastTypingRef.current = now;
        onTyping();
      }
    }
    // Bot voice Phase D: the guidance variant says "still writing guidance" on a text change (React fires onChange only when the value differs), at most once per
    // 2000 ms. A box emptied by the agent is not composing; a box cleared after Send or filled by a prefill never comes
    // through here (those are not change events).
    if (consult && onGuidanceComposing && next.trim() !== '') {
      const now = Date.now();
      const last = lastGuidanceComposingRef.current;
      if (last === null || now - last >= GUIDANCE_COMPOSING_THROTTLE_MS) {
        lastGuidanceComposingRef.current = now;
        onGuidanceComposing();
      }
    }
  };

  const submit = async () => {
    const text = v.trim();
    // An attachment with no words is a legitimate message — a screenshot is often the whole
    // reply. But an empty composer with nothing staged still sends nothing.
    if ((!text && readyIds.length === 0) || sending || isUploading) return;

    setSending(true);
    setSendError(null);
    try {
      if (consult && readyIds.length > 0) {
        // Display metadata for the agent's own echo of the guidance; the ids are what the server acts on.
        const readyAttachments: MessageAttachment[] = staged
          .filter((s) => s.status === 'ready' && s.uploadedId)
          .map((s) => ({
            fileId: s.uploadedId as string,
            fileName: s.fileName,
            mimeType: s.mimeType,
            sizeBytes: s.sizeBytes,
            kind: 'image',
          }));
        await onSend(text, readyIds, readyAttachments);
      } else {
        await onSend(text, readyIds.length > 0 ? readyIds : undefined);
      }
      setV('');
      // Cleared only on success. A failed send KEEPS the tray: the bytes are uploaded and still
      // valid, so retrying must not make the agent pick their files again.
      clearStaged();
      if (ref.current) ref.current.style.height = 'auto';
    } catch (err: unknown) {
      // submit() is called from onClick and onKeyDown, neither of which awaits it — so without
      // this catch a rejection becomes an unhandled promise rejection and the agent sees
      // NOTHING: the message simply does not send, with no error and no explanation. That is
      // the worst possible outcome for the one action they most need to trust, and it is why
      // AgentHub throws named reason codes rather than a generic failure.
      setSendError(describeError?.(err) ?? describeSendFailure(err));
    } finally {
      setSending(false);
    }
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Checked before anything else: with the feature dark there is no paperclip to hide, so
      // without this the clipboard would be a way in past the flag — and calling preventDefault
      // would break ordinary text pasting for every agent.
      if (!attachmentsAvailable) return;
      const files = filesFromClipboard(e.clipboardData);
      if (files.length === 0) return;
      e.preventDefault();
      void addFiles(files);
    },
    [attachmentsAvailable, addFiles],
  );

  const canSend = (!!v.trim() || readyIds.length > 0) && !disabled && !sending && !isUploading;

  return (
    <div className="ac-composer">
      {consult && (
        <div className="ac-composer-hint">
          <Icon name="sparkle" size={13} />
          {t('guidance_hint')}
        </div>
      )}
      {sendError && (
        <div className="ac-composer-error" role="alert" data-testid="composer-send-error">
          {sendError}
        </div>
      )}
      {attachmentsAvailable && <StagedAttachments items={staged} onRemove={removeStaged} />}
      <div className={'ac-composer-box' + (consult ? ' consult' : '')}>
        {attachmentsAvailable && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={
                mediaConfig.allowedMimeTypes.length > 0
                  ? mediaConfig.allowedMimeTypes.join(',')
                  : undefined
              }
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) void addFiles(files);
                // Reset so picking the SAME file twice in a row still fires a change event.
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              style={{ display: 'none' }}
              aria-label={consult ? 'Upload picture' : 'Upload file'}
              data-testid="composer-file-input"
            />
            <button
              className="ac-icon-btn"
              style={{ border: 'none', background: 'transparent', width: 32, height: 32 }}
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canStageMore || disabled}
              aria-label={consult ? 'Attach picture for the bot' : 'Attach file'}
              title={consult ? 'Attach picture for the bot' : 'Attach file'}
              data-testid="composer-attach"
            >
              <Icon name="clip" size={18} />
            </button>
          </>
        )}
        <textarea
          ref={ref}
          dir="auto"
          rows={1}
          value={v}
          disabled={disabled || sending}
          onChange={grow}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          data-testid={rest['data-testid'] ?? `composer-${variant}`}
        />
        <button
          className={
            'ac-send-btn ' +
            (consult ? 'consult' : 'customer') +
            (sendLabel ? ' labelled' + (tone ? ' tone-' + tone : '') : '')
          }
          disabled={!canSend}
          onClick={submit}
          aria-label={sendLabel ?? t('sender_you')}
          data-testid={`composer-send-${variant}`}
        >
          <Icon name="send" size={18} className="ac-flip" />
          {sendLabel && <span className="ac-send-label">{sendLabel}</span>}
        </button>
      </div>
    </div>
  );
};
